import React, { useState, useEffect } from 'react';
import { Box, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../contexts/OrganizationContext';
import UpgradePromptDialog from '../billing/UpgradePromptDialog';
import LimitReachedBanner from '../billing/LimitReachedBanner';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

/**
 * Wrapper component for project creation that enforces usage limits
 * Shows upgrade prompts when project limit is reached
 */
const ProjectCreationWithLimits = ({ children }) => {
  const { currentOrganization } = useOrganization();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [canCreate, setCanCreate] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentOrganization?.id) {
      checkUsageLimits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization]);

  const checkUsageLimits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/orgs/${currentOrganization.id}/usage`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const usageData = await response.json();
        setUsage(usageData);

        // Check if at limit
        const projectsUsed = usageData.usage?.projects || 0;
        const projectsLimit = usageData.limits?.projects;
        
        if (projectsLimit !== null && projectsUsed >= projectsLimit) {
          setCanCreate(false);
          setUpgradeDialogOpen(true);
        } else {
          setCanCreate(true);
        }
      }
    } catch (error) {
      console.error('Failed to check usage limits:', error);
      // Allow creation if we can't check limits
      setCanCreate(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/orgs/${currentOrganization.id}/create-session`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ plan_name: planName })
        }
      );

      if (response.ok) {
        const { url } = await response.json();
        window.location.href = url;
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
    }
  };

  const projectsUsed = usage?.usage?.projects || 0;
  const projectsLimit = usage?.limits?.projects || 0;
  const currentPlan = usage?.plan?.name || 'free';

  if (loading) {
    return null; // Or a loading spinner
  }

  // If at limit, show upgrade dialog instead of form
  if (!canCreate) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          You've reached your project limit ({projectsLimit} projects). 
          Upgrade your plan to create more projects.
        </Alert>

        <UpgradePromptDialog
          open={upgradeDialogOpen}
          onClose={() => {
            setUpgradeDialogOpen(false);
            navigate('/projects');
          }}
          onUpgrade={handleUpgrade}
          limitType="projects"
          currentPlan={currentPlan}
          suggestedPlan={currentPlan === 'free' ? 'basic' : 'pro'}
        />

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setUpgradeDialogOpen(true)}
          >
            Upgrade Now
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/projects')}
          >
            Back to Projects
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <>
      {/* Show warning banner if approaching limit */}
      {usage && canCreate && (
        <Box sx={{ mb: 2 }}>
          <LimitReachedBanner
            limitType="projects"
            current={projectsUsed}
            limit={projectsLimit}
            onUpgrade={() => navigate('/billing')}
            showProgress={true}
          />
        </Box>
      )}

      {/* Render the actual form */}
      {children}
    </>
  );
};

export default ProjectCreationWithLimits;
